import React, { useEffect, useCallback, useRef } from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import ContentGridItem from "arena/components/ContentGridItem";
import Previewable from "arena/components/Previewable";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import SkuflixChevronRightIcon from "core/icons/skuflix/SkuflixChevronRightIcon";
import useCarousel from "core/hooks/useCarousel";
import { alpha } from "@mui/system";

const PREFIX = "ContentCarousel";

const classes = {
  scrollable: `${PREFIX}-scrollable`,
  grid: `${PREFIX}-grid`,
  item: `${PREFIX}-item`,
  prev: `${PREFIX}-prev`,
  next: `${PREFIX}-next`,
  btnRoot: `${PREFIX}-btnRoot`,
  icon: `${PREFIX}-icon`,
};

const Root = styled("div")(({ theme }) => ({
  position: "relative",
  overflow: "hidden",

  [`& .${classes.scrollable}`]: {
    overflowY: "hidden",
    overflowX: "auto",
    msOverflowStyle: "none", // IE 10+
    scrollbarWidth: "none", // Firefox
    "&::-webkit-scrollbar": {
      display: "none", // Safari and Chrome
    },
    scrollSnapType: "x mandatory",
  },

  [`& .${classes.grid}`]: {
    flexWrap: "nowrap",
  },

  [`& .${classes.item}`]: {
    flexShrink: 0,
    scrollSnapAlign: "start",
  },

  [`& .${classes.prev}`]: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    zIndex: 1,
    width: 0,
    height: "100%",
    pointerEvents: "none",
    "& button": {
      left: 0,
      backgroundImage: `linear-gradient(90deg, ${
        theme.palette.background.body
      }, ${alpha(theme.palette.background.body, 0.6)}, transparent)`,
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  [`& .${classes.next}`]: {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    zIndex: 1,
    width: 0,
    height: "100%",
    pointerEvents: "none",
    "& button": {
      right: 0,
      backgroundImage: `linear-gradient(-90deg, ${
        theme.palette.background.body
      }, ${alpha(theme.palette.background.body, 0.6)}, transparent)`,
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  [`& .${classes.btnRoot}`]: {
    pointerEvents: "all",
    position: "absolute",
    top: 0,
    bottom: 0,
    width: 40,
    backgroundColor: "transparent",
    color: "rgba(255, 255, 255, 0.3)",
    "&:hover": {
      color: "#fff",
    },
    [theme.breakpoints.up("sm")]: {
      width: 60,
    },
    [theme.breakpoints.up("md")]: {
      width: 70,
    },
  },

  [`& .${classes.icon}`]: {
    fontSize: 40,
    [theme.breakpoints.up("sm")]: {
      fontSize: 50,
    },
  },
}));

export default function ContentCarousel({
  nodes,
  type,
  productId,
  collectionId,
  previewMode,
  sm,
  md,
  lg,
  xl,
  xs,
  hasMore,
  onLoadMore,
  loading,
}) {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));

  const isMobile = isXs;

  const { rootRef, onScroll, prev, next, onClickPrev, onClickNext } =
    useCarousel(nodes.length);

  // Ref для отслеживания автозагрузки при монтировании
  const autoLoadTriggeredRef = useRef(false);

  // Простая автозагрузка при монтировании - если есть место для большего количества элементов
  useEffect(() => {
    if (hasMore && onLoadMore && !loading && !autoLoadTriggeredRef.current) {
      // Определяем сколько элементов помещается в одном ряду на текущем экране
      const itemsPerRow = isXs ? 12 / (xs || 12) : 12 / (md || 4);

      // Автозагрузка только если элементов достаточно для заполнения экрана
      if (nodes.length >= itemsPerRow) {
        autoLoadTriggeredRef.current = true;
        onLoadMore();
      }
    }
  }, [onLoadMore, nodes.length, xs, md, isXs]); // Добавили зависимости для grid

  // Обработчик скролла с поддержкой LoadMore
  const handleScroll = useCallback(
    (event) => {
      onScroll(event);

      // Проверяем нужно ли загрузить еще при скролле
      if (hasMore && onLoadMore && !loading) {
        const { scrollWidth, scrollLeft, clientWidth } = event.target;
        const totalScrollableWidth = scrollWidth - clientWidth;
        const scrollPercentage = scrollLeft / totalScrollableWidth;

        if (scrollPercentage >= 0.8) {
          onLoadMore();
        }
      }
    },
    [hasMore, onLoadMore, loading, onScroll]
  );

  if (!nodes || nodes.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{ pl: { xs: 2, sm: 0 }, pr: { xs: 2, sm: 0 } }}
      test-id="ContentCarousel"
    >
      <Root>
        <div
          className={classes.scrollable}
          ref={rootRef}
          onScroll={handleScroll}
        >
          <Grid container spacing={2} className={classes.grid}>
            {nodes.map((content) => (
              <Grid
                item
                xs={xs}
                sm={sm}
                md={md}
                lg={lg}
                xl={xl}
                key={content.id}
                className={classes.item}
              >
                <Previewable isMobile={isMobile}>
                  {(focused) => (
                    <ContentGridItem
                      content={content}
                      play={focused}
                      type={type}
                      productId={productId}
                      collectionId={collectionId}
                      previewMode={previewMode}
                      smallTitle={12 / md > 3}
                    />
                  )}
                </Previewable>
              </Grid>
            ))}
          </Grid>
        </div>

        {prev && (
          <div className={classes.prev}>
            <ButtonBase
              classes={{ root: classes.btnRoot }}
              onClick={onClickPrev}
            >
              <SkuflixChevronRightIcon
                className={classes.icon}
                sx={{ transform: "scaleX(-1)" }}
              />
            </ButtonBase>
          </div>
        )}

        {next && (
          <div className={classes.next}>
            <ButtonBase
              classes={{ root: classes.btnRoot }}
              onClick={onClickNext}
            >
              <SkuflixChevronRightIcon className={classes.icon} />
            </ButtonBase>
          </div>
        )}
      </Root>
    </Box>
  );
}

ContentCarousel.defaultProps = {
  xs: 6,
  sm: 6,
  md: 4,
  lg: 4,
  xl: 4,
  nodes: [],
  hasMore: false,
  onLoadMore: null,
  loading: false,
};

ContentCarousel.propTypes = {
  xs: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  sm: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  md: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  lg: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  xl: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  type: PropTypes.string,
  collectionId: PropTypes.string,
  productId: PropTypes.string,
  previewMode: PropTypes.oneOf(["HORIZONTAL", "VERTICAL"]),
  nodes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    })
  ),
  hasMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
  loading: PropTypes.bool,
};
